import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fileUrl, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Plane, MapPin } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";

export default function Trips() {
  const { user } = useAuth();
  const isJastiper = user?.role === "jastiper";
  const isBuyer = user?.role === "buyer";
  const [trips, setTrips] = useState([]);
  const [countries, setCountries] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const country = params.get("country") || "";
  const origin = params.get("origin") || "";
  const mine = params.get("mine") === "true";

  useEffect(() => {
    api.get("/countries").then((r) => setCountries(r.data));
    api.get("/locations").then((r) => setLocations(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (country) q.set("country", country);
    if (origin) q.set("origin", origin);
    if (mine) q.set("mine", "true");
    api.get(`/trips?${q.toString()}`).then((r) => setTrips(r.data)).finally(() => setLoading(false));
  }, [country, origin, mine]);

  const setCountry = (c) => {
    const p = new URLSearchParams(params);
    if (c) p.set("country", c); else p.delete("country");
    setParams(p);
  };

  const setOrigin = (o) => {
    const p = new URLSearchParams(params);
    if (o) p.set("origin", o); else p.delete("origin");
    setParams(p);
  };

  const setMine = (m) => {
    const p = new URLSearchParams(params);
    if (m) p.set("mine", "true"); else p.delete("mine");
    setParams(p);
  };

  // Flatten locations for dropdown
  const originOptions = locations.flatMap((p) => p.cities.map((c) => ({ value: c, label: `${c}, ${p.province}` })));

  return (
    <div className="space-y-8" data-testid="trips-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <div className="label-caps mb-2">Personal Shoppers</div>
          <h1 className="font-display text-4xl tracking-tight">
            {isJastiper && mine ? "Trip Saya" : "Trip Aktif"}
          </h1>
          <p className="mt-2" style={{color: "var(--app-muted)"}}>
            {isBuyer ? "Jelajahi Jastiper yang sedang travel dan pesan dari katalog mereka." : "Jastiper menuju destinasi ini."}
          </p>
        </div>
        {isJastiper && (
          <Link to="/trips/new" className="btn-primary-flat" data-testid="create-trip-btn">
            <Plus size={16} strokeWidth={1.5} /> Posting Trip
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="surface p-4 flex flex-col sm:flex-row gap-3 sm:items-center">
        {isJastiper && (
          <div className="flex gap-2">
            <button
              onClick={() => setMine(false)}
              className="px-4 py-2 text-sm border rounded-md"
              style={{
                backgroundColor: !mine ? "var(--app-brand)" : "var(--app-surface)",
                color: !mine ? "white" : "var(--app-fg)",
                borderColor: !mine ? "var(--app-brand)" : "var(--app-border)",
              }}
              data-testid="filter-all-trips"
            >Semua Trip</button>
            <button
              onClick={() => setMine(true)}
              className="px-4 py-2 text-sm border rounded-md"
              style={{
                backgroundColor: mine ? "var(--app-brand)" : "var(--app-surface)",
                color: mine ? "white" : "var(--app-fg)",
                borderColor: mine ? "var(--app-brand)" : "var(--app-border)",
              }}
              data-testid="filter-my-trips"
            >Trip Saya</button>
          </div>
        )}
        <div className="flex-1" />
        <select
          value={origin}
          onChange={(e) => setOrigin(e.target.value)}
          className="input-flat sm:w-56"
          data-testid="filter-origin"
        >
          <option value="">Semua Asal</option>
          {originOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className="input-flat sm:w-56"
          data-testid="filter-country"
        >
          <option value="">Semua Negara</option>
          {countries.map((c) => (
            <option key={c.code} value={c.name}>{c.flag} {c.name}</option>
          ))}
        </select>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-center py-20" style={{color: "var(--app-muted)"}}>Memuat...</div>
      ) : trips.length === 0 ? (
        <div className="surface py-20 text-center" data-testid="empty-trips">
          <Plane size={32} strokeWidth={1.5} className="mx-auto mb-3" style={{color: "var(--app-muted)"}} />
          <p style={{color: "var(--app-muted)"}}>Tidak ada trip ditemukan</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-fade">
          {trips.map((trip) => (
            <TripCard key={trip.id} trip={trip} />
          ))}
        </div>
      )}
    </div>
  );
}

function TripCard({ trip }) {
  const itemCount = trip.catalog_items?.length || 0;
  const previewItem = trip.catalog_items?.[0];
  return (
    <Link to={`/trips/${trip.id}`} className="grid-card group block" data-testid={`trip-card-${trip.id}`}>
      <div className="aspect-[16/10] overflow-hidden relative" style={{backgroundColor: "var(--app-surface-alt)"}}>
        {previewItem?.photo_path ? (
          <img src={fileUrl(previewItem.photo_path)} alt={previewItem.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
            <Plane size={40} strokeWidth={1} style={{color: "var(--app-brand)", opacity: 0.4}} />
          </div>
        )}
        <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-2 py-1 label-caps">
          {itemCount} {itemCount === 1 ? "item" : "item"}
        </div>
        {trip.origin_location && (
          <div className="absolute top-3 right-3 backdrop-blur px-2 py-1 text-[10px] font-mono uppercase tracking-wide rounded text-white" style={{backgroundColor: "var(--app-brand)"}}>
            dari {trip.origin_location}
          </div>
        )}
      </div>
      <div className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-display text-lg tracking-tight flex items-center gap-1.5">
              <MapPin size={14} strokeWidth={1.5} style={{color: "var(--app-brand)"}} />
              {trip.destination_country}
            </div>
            {trip.destination_city && <div className="text-xs" style={{color: "var(--app-muted)"}}>{trip.destination_city}</div>}
          </div>
        </div>
        <div className="border-t pt-3 font-mono text-xs flex justify-between" style={{borderColor: "var(--app-border)", color: "var(--app-muted)"}}>
          <span>{formatDate(trip.departure_date)} → {formatDate(trip.return_date)}</span>
        </div>
        <div className="flex items-center justify-between pt-1">
          <div className="text-sm">
            oleh <span className="font-medium inline-flex items-center gap-1">
              {trip.jastiper_name}
              {trip.jastiper_verified_badge && <VerifiedBadge size={12} testid={`trip-card-verified-${trip.id}`} />}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
